﻿import { expressQuerySelector, hasClass, isElementVisibleInViewPort } from '../common/html';
import { createClickableElement } from './button';

export interface ICreateCollapsableDependencies {
	onBeforeOpen?: (container: HTMLElement) => Promise<void>;
	readonly onBeforeClose?: (container: HTMLElement) => Promise<void>;
	readonly titleElSelector: string;
	readonly scrollIntoViewAfterOpen: boolean;
	readonly contentSelector?: string;
	readonly openClass?: string;
}

export interface ICollapsibleContentRequest {
	readonly type: string;
}

export interface ICollapsable {
	dispose(): void;
	show(): void;
	close(): void;
	hide(): void;
	open(scrollIntoView?: boolean): void;
	element: HTMLElement;
}

export function createCollapsable(container: HTMLElement, deps: ICreateCollapsableDependencies): ICollapsable {
	const { onBeforeOpen, onBeforeClose, titleElSelector, scrollIntoViewAfterOpen, openClass, contentSelector } = deps;
	const OPEN_CLASS = openClass ? openClass : "is-collapse-open";
	const titleEl = expressQuerySelector<HTMLElement>(container, titleElSelector, true);
	const contentEl = expressQuerySelector<HTMLElement>(container, contentSelector ? contentSelector : '.technical-collapse-content', false);

	const closeWithBefore = () => {
		if (!onBeforeClose) {
			close();
			return Promise.resolve();
		}

		return onBeforeClose(container).then(() => close());
	};

	const openWithBefore = (scrollIntoView = false) => {
		if (!onBeforeOpen) {
			open();
			return Promise.resolve();
		}

		return onBeforeOpen(container).then(() => open(scrollIntoView));
	};

	const close = () => container.classList.remove(OPEN_CLASS);
	const open = (shouldScrollIntoView?: boolean) => {
		container.classList.add(OPEN_CLASS);
		if ((scrollIntoViewAfterOpen && !isElementVisibleInViewPort(contentEl)) || shouldScrollIntoView)
			titleEl.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const isOpen = () => hasClass(container, OPEN_CLASS);

	const onClick = () => !isOpen() ? openWithBefore() : closeWithBefore();

	const button = createClickableElement(titleEl, onClick);

	return {
		dispose: button.dispose,
		show: () => container.classList.remove('u-hide'),
		hide: () => container.classList.add('u-hide'),
		close,
		open: (scrollIntoView?: boolean) => openWithBefore(scrollIntoView),
		element: container
	};
}
